import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { money, timeAgo } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Truck, Trash2, PackageCheck } from "lucide-react";

export default function Vendors() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [poOpen, setPoOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", supplies: "" });
  const [po, setPo] = useState({ vendor_id: "", lines: [] });

  const { data: vendors = [] } = useQuery({ queryKey: ["vendors"], queryFn: async () => (await api.get("/vendors")).data });
  const { data: inventory = [] } = useQuery({ queryKey: ["inventory"], queryFn: async () => (await api.get("/inventory")).data });
  const { data: purchases = [] } = useQuery({ queryKey: ["purchases"], queryFn: async () => (await api.get("/purchases")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const sym = settings?.currency_symbol || "₹";

  const addVendor = async () => {
    if (!form.name) return toast.error("Name required");
    await api.post("/vendors", form); toast.success("Vendor added"); setOpen(false);
    setForm({ name: "", phone: "", email: "", address: "", supplies: "" });
    qc.invalidateQueries({ queryKey: ["vendors"] });
  };
  const delVendor = async (v) => { await api.delete(`/vendors/${v.id}`); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["vendors"] }); };

  const openPo = (v) => { setPo({ vendor_id: v.id, vendor_name: v.name, lines: [] }); setPoOpen(true); };
  const addLine = () => setPo((p) => ({ ...p, lines: [...p.lines, { inventory_id: inventory[0]?.id || "", qty: 1, cost: 0 }] }));
  const submitPo = async () => {
    if (!po.lines.length) return toast.error("Add at least one item");
    const items = po.lines.map((l) => { const inv = inventory.find((i) => i.id === l.inventory_id); return { inventory_id: l.inventory_id, name: inv?.name || "", qty: Number(l.qty), cost: Number(l.cost) }; });
    await api.post("/purchases", { vendor_id: po.vendor_id, vendor_name: po.vendor_name, items, status: "received" });
    toast.success("Stock received & inventory updated"); setPoOpen(false);
    qc.invalidateQueries({ queryKey: ["purchases"] }); qc.invalidateQueries({ queryKey: ["inventory"] });
  };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8">
      <PageHeader title="Vendors & Purchasing" subtitle="Suppliers and stock restocking">
        <Button onClick={() => setOpen(true)} data-testid="add-vendor-btn" className="bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white"><Plus className="w-4 h-4 mr-1" /> Add Vendor</Button>
      </PageHeader>

      <Tabs defaultValue="vendors">
        <TabsList><TabsTrigger value="vendors" data-testid="tab-vendors">Vendors</TabsTrigger><TabsTrigger value="orders" data-testid="tab-purchases">Purchase Orders</TabsTrigger></TabsList>
        <TabsContent value="vendors">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {vendors.map((v) => (
              <div key={v.id} className="kul-card p-4" data-testid={`vendor-${v.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2"><div className="w-9 h-9 rounded-lg bg-[var(--kul-primary)]/10 flex items-center justify-center"><Truck className="w-4 h-4 text-[var(--kul-primary)]" /></div><div><div className="font-semibold">{v.name}</div><div className="text-xs text-neutral-400">{v.phone}</div></div></div>
                  <button onClick={() => delVendor(v)} className="text-neutral-300 hover:text-[var(--kul-danger)]"><Trash2 className="w-4 h-4" /></button>
                </div>
                <p className="text-sm text-neutral-500 mt-3">{v.supplies}</p>
                <p className="text-xs text-neutral-400 mt-1">{v.address}</p>
                <Button size="sm" variant="outline" className="w-full mt-3" onClick={() => openPo(v)} data-testid={`restock-${v.id}`}><PackageCheck className="w-4 h-4 mr-1" /> Create Restock</Button>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="orders">
          <div className="space-y-2">
            {purchases.map((p) => (
              <div key={p.id} className="kul-card p-4 flex items-center justify-between" data-testid={`po-${p.id}`}>
                <div><div className="font-semibold">{p.vendor_name}</div><div className="text-sm text-neutral-500">{p.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</div><div className="text-xs text-neutral-400">{timeAgo(p.created_at)} · {p.status}</div></div>
                <span className="font-mono-k font-semibold">{money(p.total, sym)}</span>
              </div>
            ))}
            {!purchases.length && <p className="text-neutral-400 text-center py-12">No purchase orders yet.</p>}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>Add Vendor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="vendor-phone" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>Supplies</Label><Input value={form.supplies} onChange={(e) => setForm({ ...form, supplies: e.target.value })} placeholder="Vegetables, Dairy…" /></div>
          </div>
          <DialogFooter><Button onClick={addVendor} className="bg-[var(--kul-primary)] text-white" data-testid="save-vendor">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={poOpen} onOpenChange={setPoOpen}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Restock from {po.vendor_name}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            {po.lines.map((l, i) => (
              <div key={i} className="flex gap-2 items-center">
                <select value={l.inventory_id} onChange={(e) => setPo((p) => ({ ...p, lines: p.lines.map((x, xi) => xi === i ? { ...x, inventory_id: e.target.value } : x) }))} className="h-9 rounded-lg border px-2 text-sm flex-1">
                  {inventory.map((inv) => <option key={inv.id} value={inv.id}>{inv.name}</option>)}
                </select>
                <Input type="number" value={l.qty} onChange={(e) => setPo((p) => ({ ...p, lines: p.lines.map((x, xi) => xi === i ? { ...x, qty: e.target.value } : x) }))} placeholder="Qty" className="h-9 w-20" />
                <Input type="number" value={l.cost} onChange={(e) => setPo((p) => ({ ...p, lines: p.lines.map((x, xi) => xi === i ? { ...x, cost: e.target.value } : x) }))} placeholder="Cost" className="h-9 w-24" />
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addLine} data-testid="po-add-line"><Plus className="w-3 h-3 mr-1" /> Add line</Button>
          </div>
          <DialogFooter><Button onClick={submitPo} className="bg-[var(--kul-primary)] text-white" data-testid="submit-po">Receive Stock</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
